import React, { useState, useMemo } from 'react';
import { Search, ChevronRight, ChevronLeft, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { LoadingSpinner } from '../ui/loading-states';
import { useEmployees, type Employee } from '../../hooks/useEmployees';
import {
  useLocationEmployees,
  useAssignEmployees,
  useUnassignEmployees,
} from '../../hooks/locations/useEmployeeLocations';

interface EmployeeAssignmentProps {
  mode: 'location-to-employees' | 'employee-to-locations';
  entityId: string;
  entityName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const EmployeeAssignment: React.FC<EmployeeAssignmentProps> = ({
  mode,
  entityId,
  entityName,
  isOpen,
  onClose,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [selectedAssigned, setSelectedAssigned] = useState<Set<string>>(new Set());

  // Debounce search для серверного поиска
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  // Загружаем всех сотрудников с серверным поиском
  const { data: allEmployees, isLoading: isLoadingEmployees, error: employeesError } = useEmployees({
    // 1000 — потолок limit на бэкенде (PaginationQueryDto). Раньше стояло 5000 и
    // работало только потому, что лимит нигде не проверялся. При пустом поиске
    // список обрезается; постраничная загрузка — отдельная задача.
    limit: 1000,
    search: debouncedSearch || undefined, // Серверный поиск
  });


  // Загружаем привязанных сотрудников
  const { data: assignedData, isLoading: isLoadingAssigned, error: assignedError } = useLocationEmployees(
    mode === 'location-to-employees' ? entityId : undefined
  );


  const assignMutation = useAssignEmployees();
  const unassignMutation = useUnassignEmployees();

  // Получаем массив привязанных сотрудников
  const assignedEmployeeIds = useMemo(() => {
    if (!assignedData || !Array.isArray(assignedData)) {
      return new Set();
    }
    return new Set(assignedData.map((el: any) => el.employee_id));
  }, [assignedData]);

  // Разделяем на доступных и привязанных (фильтрация уже на сервере)
  const { availableEmployees, assignedEmployees } = useMemo(() => {
    const employees = allEmployees?.data || [];

    // Фильтрация по поиску теперь на сервере, здесь только разделение на доступных/привязанных
    const available = employees.filter((emp) => !assignedEmployeeIds.has(emp.id));
    const assigned = employees.filter((emp) => assignedEmployeeIds.has(emp.id));

    return {
      availableEmployees: available,
      assignedEmployees: assigned,
    };
  }, [allEmployees, assignedEmployeeIds, debouncedSearch]);

  const handleAssign = async () => {
    if (selectedAvailable.size === 0) return;

    const assignData = {
      employee_ids: Array.from(selectedAvailable),
      location_ids: [entityId], // Всегда используем entityId как location_id
      access_level: 'standard' as const,
    };

    try {
      await assignMutation.mutateAsync(assignData);
      setSelectedAvailable(new Set());
    } catch (error) {
      console.error('Failed to assign employees:', error);
    }
  };

  const handleUnassign = async () => {
    if (selectedAssigned.size === 0) return;

    try {
      await unassignMutation.mutateAsync({
        employee_ids: Array.from(selectedAssigned),
        location_ids: [entityId], // Всегда используем entityId как location_id
      });
      setSelectedAssigned(new Set());
    } catch (error) {
      console.error('Failed to unassign employees:', error);
    }
  };

  const toggleSelectAvailable = (id: string) => {
    const newSet = new Set(selectedAvailable);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedAvailable(newSet);
  };

  const toggleSelectAssigned = (id: string) => {
    const newSet = new Set(selectedAssigned);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedAssigned(newSet);
  };

  const selectAllAvailable = () => {
    setSelectedAvailable(new Set(availableEmployees.map((e) => e.id)));
  };

  const selectAllAssigned = () => {
    setSelectedAssigned(new Set(assignedEmployees.map((e) => e.id)));
  };

  const isLoading = isLoadingEmployees || isLoadingAssigned;
  const isMutating = assignMutation.isPending || unassignMutation.isPending;

  const renderEmployee = (
    employee: Employee,
    isSelected: boolean,
    onToggle: (id: string) => void
  ) => (
    <div
      key={employee.id}
      className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
        isSelected 
          ? 'bg-blue-50 border-blue-300 shadow-sm' 
          : 'hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
      }`}
      onClick={() => onToggle(employee.id)}
    >
      <Checkbox 
        checked={isSelected} 
        onChange={() => onToggle(employee.id)}
        className="flex-shrink-0"
      />
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="font-medium text-gray-900 break-words">
          {employee.last_name} {employee.first_name} {employee.middle_name}
        </div>
        <div className="text-sm text-gray-500 break-words">
          {employee.position?.name || 'Без должности'}
          {employee.department?.name && ` • ${employee.department.name}`}
        </div>
      </div>
      <div className="flex-shrink-0 ml-2">
        {employee.is_active ? (
          <Badge variant="success" className="text-xs">Активен</Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">Неактивен</Badge>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-7xl h-[95vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Привязка сотрудников</DialogTitle>
          <DialogDescription>
            {mode === 'location-to-employees'
              ? `Управление сотрудниками для локации "${entityName}"`
              : `Управление локациями для сотрудника "${entityName}"`}
          </DialogDescription>
        </DialogHeader>

        {/* Поиск */}
        <div className="relative flex-shrink-0 mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Поиск сотрудников..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex gap-4 flex-1">
            {/* Доступные */}
            <div className="flex flex-col w-1/2">
              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <h3 className="font-medium text-lg">
                  Доступные ({availableEmployees.length})
                </h3>
                <Button size="sm" variant="ghost" onClick={selectAllAvailable}>
                  Выбрать все
                </Button>
              </div>
              <div className="h-[500px] overflow-y-auto border rounded-lg p-3 space-y-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {availableEmployees.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="mx-auto h-12 w-12 mb-3" />
                    <p className="text-lg">Нет доступных сотрудников</p>
                  </div>
                ) : (
                  availableEmployees.map((employee) =>
                    renderEmployee(
                      employee,
                      selectedAvailable.has(employee.id),
                      toggleSelectAvailable
                    )
                  )
                )}
              </div>
            </div>

            {/* Кнопки переноса */}
            <div className="flex flex-col justify-center items-center gap-3 flex-shrink-0 px-2">
              <Button
                size="sm"
                onClick={handleAssign}
                disabled={selectedAvailable.size === 0 || isMutating}
                className="w-10 h-10 rounded-full"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
              <Button
                size="sm"
                onClick={handleUnassign}
                disabled={selectedAssigned.size === 0 || isMutating}
                className="w-10 h-10 rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </div>

            {/* Привязанные */}
            <div className="flex flex-col w-1/2">
              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <h3 className="font-medium text-lg">
                  Привязанные ({assignedEmployees.length})
                </h3>
                <Button size="sm" variant="ghost" onClick={selectAllAssigned}>
                  Выбрать все
                </Button>
              </div>
              <div className="h-[500px] overflow-y-auto border rounded-lg p-3 space-y-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {assignedEmployees.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="mx-auto h-12 w-12 mb-3" />
                    <p className="text-lg">Нет привязанных сотрудников</p>
                  </div>
                ) : (
                  assignedEmployees.map((employee) =>
                    renderEmployee(
                      employee,
                      selectedAssigned.has(employee.id),
                      toggleSelectAssigned
                    )
                  )
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-gray-500">
              Выбрано: {selectedAvailable.size + selectedAssigned.size}
            </div>
            <Button variant="outline" onClick={onClose}>
              Закрыть
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};